import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { WifiOff, Wifi, RefreshCw, Cloud, CloudOff, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getPendingSyncCount, isOnline } from "../utils/offlineSync";

const OfflineIndicator = ({ syncManager, onSync }) => {
  const [online, setOnline] = useState(isOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = async () => {
      setOnline(true);
      toast.success("Connessione ripristinata! Sincronizzazione in corso...");
      await handleSync();
    };

    const handleOffline = () => {
      setOnline(false);
      setShowBanner(true);
      toast.warning("Sei offline. Le modifiche verranno salvate localmente.");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state
    setOnline(navigator.onLine);
    updatePendingCount();

    // Set up sync manager callbacks
    if (syncManager) {
      syncManager.onSyncComplete = (results) => {
        setIsSyncing(false);
        updatePendingCount();
        if (results.success.length > 0) {
          toast.success(`Sincronizzati ${results.success.length} elementi`);
        }
        if (results.failed.length > 0) {
          toast.error(`${results.failed.length} elementi non sincronizzati`);
        }
        if (onSync) onSync(results);
      };
      
      syncManager.onSyncError = (error) => {
        setIsSyncing(false);
        toast.error("Errore durante la sincronizzazione");
      };
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncManager, onSync]);

  const updatePendingCount = async () => {
    try {
      const count = await getPendingSyncCount();
      setPendingCount(count);
    } catch (error) {
      console.error("Error getting pending count:", error);
    }
  };

  const handleSync = async () => {
    if (!syncManager || !online) return;
    
    setIsSyncing(true);
    try {
      await syncManager.sync();
    } catch (error) {
      console.error("Sync error:", error);
      setIsSyncing(false);
    }
    await updatePendingCount();
  };

  // Don't show anything if online and no pending items
  if (online && pendingCount === 0 && !showBanner) return null;

  return (
    <>
      {/* Offline Banner */}
      {!online && (
        <div 
          className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 py-2 px-4 flex items-center justify-center gap-2 text-sm font-medium"
          data-testid="offline-banner"
        >
          <WifiOff className="w-4 h-4" />
          <span>Modalità offline - Le modifiche verranno sincronizzate quando tornerai online</span>
          {pendingCount > 0 && (
            <Badge variant="secondary" className="ml-2 bg-amber-600 text-white">
              {pendingCount} in attesa
            </Badge>
          )}
        </div>
      )}

      {/* Sync Status Indicator (small floating badge when online with pending items) */}
      {online && pendingCount > 0 && (
        <div 
          className="fixed bottom-4 left-4 z-40 flex items-center gap-2 bg-card border border-border rounded-lg shadow-lg p-2 animate-in slide-in-from-left"
          data-testid="sync-indicator"
        >
          {isSyncing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm">Sincronizzazione...</span>
            </>
          ) : (
            <>
              <Cloud className="w-4 h-4 text-amber-500" />
              <span className="text-sm">{pendingCount} da sincronizzare</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSync}
                className="h-7 px-2"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Sincronizza
              </Button>
            </>
          )}
        </div>
      )}

      {/* Sync Complete Toast */}
      {online && pendingCount === 0 && showBanner && (
        <div 
          className="fixed bottom-4 left-4 z-40 flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-600 rounded-lg shadow-lg p-3 animate-in slide-in-from-left"
          data-testid="sync-complete"
        >
          <Check className="w-4 h-4" />
          <span className="text-sm">Tutto sincronizzato!</span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowBanner(false)}
            className="h-6 w-6 p-0"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}
    </>
  );
};

export default OfflineIndicator;
